/* Presentation only: native forms, cart, lead requests and tracking remain owned by the theme. */
(function () {
  'use strict';
  if (window.HLXNative) return;

  const controllers = new Map();
  const disposers = [];
  const mobile = window.matchMedia('(max-width: 768px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let started = false;

  function listen(target, event, callback, options) {
    target.addEventListener(event, callback, options);
    return () => target.removeEventListener(event, callback, options);
  }

  function find(scope, selector) {
    const elements = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) elements.unshift(scope);
    return elements;
  }

  function mountGallery(root) {
    const track = root.querySelector('[data-hlx-gallery-track]');
    const items = track && Array.from(track.querySelectorAll('[data-hlx-gallery-item]'));
    if (!track || !items.length) return () => {};
    const range = root.querySelector('[data-hlx-gallery-range]');
    const status = root.querySelector('[data-hlx-gallery-status]');
    const buttons = Array.from(root.querySelectorAll('[data-hlx-gallery-step]'));
    const cleanup = [];
    let frame = 0;
    const maximum = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const distance = () => Math.min(maximum(), Math.abs(track.scrollLeft));
    const rtl = () => window.getComputedStyle(track).direction === 'rtl';
    const positions = () => {
      const first = items[0].getBoundingClientRect().left;
      return items.map(item => Math.abs(item.getBoundingClientRect().left - first));
    };

    function sync() {
      frame = 0;
      const maximumDistance = maximum();
      const current = distance();
      const offsets = positions();
      const nearest = offsets.reduce((best, value, index) =>
        Math.abs(value - current) < Math.abs(offsets[best] - current) ? index : best, 0);
      const label = `${nearest + 1} / ${items.length}`;
      if (range) {
        range.min = '0';
        range.max = '100';
        range.step = '1';
        range.value = maximumDistance ? String(Math.round(current / maximumDistance * 100)) : '0';
        range.disabled = maximumDistance <= 1;
        range.setAttribute('aria-valuetext', label);
      }
      if (status) status.textContent = label;
      buttons.forEach(button => {
        button.disabled = Number(button.dataset.hlxGalleryStep) < 0
          ? current <= 1 : current >= maximumDistance - 1;
      });
    }

    function scheduleSync() {
      if (!frame) frame = window.requestAnimationFrame(sync);
    }

    function goTo(destination, immediate) {
      const next = Math.max(0, Math.min(maximum(), destination));
      track.scrollTo({
        left: rtl() ? -next : next,
        behavior: immediate || reducedMotion.matches ? 'instant' : 'smooth',
      });
      scheduleSync();
    }

    function step(direction) {
      const current = distance();
      const offsets = positions();
      const next = direction > 0
        ? offsets.find(offset => offset > current + 1)
        : offsets.reverse().find(offset => offset < current - 1);
      goTo(next === undefined ? (direction > 0 ? maximum() : 0) : next);
    }

    buttons.forEach(button => cleanup.push(listen(button, 'click', () => {
      step(Number(button.dataset.hlxGalleryStep));
    })));
    if (range) cleanup.push(listen(range, 'input', () => {
      const percent = Number(range.value);
      if (Number.isFinite(percent)) goTo(maximum() * percent / 100, true);
    }));
    cleanup.push(listen(track, 'scroll', scheduleSync, { passive: true }));
    cleanup.push(listen(track, 'load', scheduleSync, true));
    cleanup.push(listen(track, 'keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        goTo(event.key === 'Home' ? 0 : maximum());
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        step((event.key === 'ArrowRight' ? 1 : -1) * (rtl() ? -1 : 1));
      }
    }));
    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(scheduleSync);
      observer.observe(track);
      items.forEach(item => observer.observe(item));
      cleanup.push(() => observer.disconnect());
    } else {
      cleanup.push(listen(window, 'resize', scheduleSync, { passive: true }));
    }
    sync();
    return () => {
      cleanup.forEach(dispose => dispose());
      if (frame) window.cancelAnimationFrame(frame);
    };
  }

  function updateHeaderHeight() {
    const header = document.querySelector('.hlx-header');
    if (header) document.documentElement.style.setProperty(
      '--hlx-header-height', `${Math.ceil(header.getBoundingClientRect().height)}px`
    );
  }

  function mountMenu(menu) {
    const toggle = Array.from(document.querySelectorAll('[data-hlx-menu-toggle]'))
      .find(button => button.getAttribute('aria-controls') === menu.id);
    if (!toggle) return () => {};
    const cleanup = [];
    let open = false;

    function setOpen(value, restoreFocus) {
      open = mobile.matches && value;
      menu.hidden = mobile.matches && !open;
      toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('hlx-menu-open', open);
      updateHeaderHeight();
      if (restoreFocus) toggle.focus({ preventScroll: true });
    }

    cleanup.push(listen(toggle, 'click', () => setOpen(!open)));
    find(menu, '[data-hlx-menu-close]').forEach(button => {
      cleanup.push(listen(button, 'click', () => setOpen(false, true)));
    });
    cleanup.push(listen(menu, 'click', event => {
      if (event.target.closest('a[href]')) setOpen(false);
    }));
    cleanup.push(listen(document, 'click', event => {
      if (open && !menu.contains(event.target) && !toggle.contains(event.target)) setOpen(false);
    }));
    cleanup.push(listen(document, 'keydown', event => {
      if (open && event.key === 'Escape') {
        event.preventDefault();
        setOpen(false, true);
      }
    }));
    cleanup.push(listen(mobile, 'change', () => setOpen(false)));
    setOpen(false);
    return () => {
      cleanup.forEach(dispose => dispose());
      menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('hlx-menu-open');
    };
  }

  function mountHeader(header) {
    updateHeaderHeight();
    if (!window.ResizeObserver) return listen(window, 'resize', updateHeaderHeight, { passive: true });
    const observer = new window.ResizeObserver(updateHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }

  function refresh(scope) {
    if (!document.body.classList.contains('hlx-native')) return;
    [
      ['[data-hlx-gallery]', mountGallery],
      ['[data-hlx-menu]', mountMenu],
      ['.hlx-header', mountHeader],
    ].forEach(([selector, mount]) => find(scope || document, selector).forEach(root => {
      if (!controllers.has(root)) controllers.set(root, mount(root));
    }));
  }

  function unload(scope) {
    controllers.forEach((dispose, root) => {
      if (scope === root || scope.contains(root)) {
        dispose();
        controllers.delete(root);
      }
    });
  }

  function start() {
    if (started || !document.body.classList.contains('hlx-native')) return;
    started = true;
    refresh(document);
    disposers.push(listen(document, 'shopify:section:load', event => refresh(event.target)));
    disposers.push(listen(document, 'shopify:section:unload', event => unload(event.target)));
  }

  window.HLXNative = {
    refresh,
    destroy() {
      unload(document);
      disposers.forEach(dispose => dispose());
      disposers.length = 0;
      document.documentElement.style.removeProperty('--hlx-header-height');
      delete window.HLXNative;
    },
  };
  if (document.readyState === 'loading') disposers.push(listen(document, 'DOMContentLoaded', start, { once: true }));
  else start();
})();
